import React, { useEffect, useState, useCallback } from "react";
import { api, uploadFile } from "../../api/client";
import ConfirmModal from "../../components/ConfirmModal";

const API_BASE = process.env.REACT_APP_API_BASE || "/api";
const EMPTY = { nom: "", description: "", contact: "", lien_web: "", ordre: 0, publie: true };

export default function AssociationsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { setItems((await api.get("/admin/associations")).items || []); }
    catch (e) { setMsg(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try { await api.del(`/admin/associations/${confirmDel.id}`); setConfirmDel(null); load(); }
    catch (e) { setMsg(e.message); }
  };

  if (editing) {
    return <AssociationForm initial={editing} onClose={() => { setEditing(null); load(); }} />;
  }

  return (
    <div>
      {msg && <div className="error">{msg}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p className="muted" style={{ margin: 0 }}>
          Les associations publiées apparaissent sur la page « Vie locale » et
          sont comptées sur la page d'accueil.
        </p>
        <button onClick={() => setEditing(EMPTY)}>+ Nouvelle association</button>
      </div>

      <div className="table-wrap mt-2">
        <table>
          <thead>
            <tr><th></th><th>Nom</th><th>Contact</th><th>Lien</th><th>Ordre</th><th>État</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id}>
                <td>
                  {a.logo_url ? (
                    <img src={`${API_BASE}${a.logo_url}`} alt=""
                         style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 4 }} />
                  ) : <span className="muted">—</span>}
                </td>
                <td>{a.nom}</td>
                <td className="muted">{a.contact || "—"}</td>
                <td>{a.lien_web ? <a href={a.lien_web} target="_blank" rel="noreferrer">lien ↗</a> : "—"}</td>
                <td>{a.ordre}</td>
                <td>
                  <span className={`badge ${a.publie ? "badge-publie" : "badge-brouillon"}`}>
                    {a.publie ? "Publiée" : "Masquée"}
                  </span>
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="secondary small" onClick={() => setEditing(a)}>Modifier</button>{" "}
                  <button className="danger small" onClick={() => setConfirmDel(a)}>Suppr.</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="muted">Aucune association enregistrée.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {confirmDel && (
        <ConfirmModal titre="Supprimer l'association" danger
          message={`Supprimer « ${confirmDel.nom} » ?`}
          confirmLabel="Supprimer" onConfirm={remove} onCancel={() => setConfirmDel(null)} />
      )}
    </div>
  );
}

function AssociationForm({ initial, onClose }) {
  const [f, setF] = useState({
    nom: initial.nom || "", description: initial.description || "",
    contact: initial.contact || "", lien_web: initial.lien_web || "",
    ordre: initial.ordre || 0, publie: initial.publie !== false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [logoUrl, setLogoUrl] = useState(initial.logo_url || null);
  const isNew = !initial.id;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      let id = initial.id;
      if (isNew) {
        const r = await api.post("/admin/associations", f);
        id = r.id;
      } else {
        await api.put(`/admin/associations/${id}`, f);
      }
      onClose();
    } catch (e) { setErr(e.message || "Erreur"); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || isNew) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await uploadFile(`/admin/associations/${initial.id}/logo`, fd);
      setLogoUrl(r.logo_url);
    } catch (er) { setErr(er.message || "Échec de l'upload"); }
  };

  return (
    <div>
      <button className="secondary mb-2" onClick={onClose}>← Retour</button>
      <h3>{isNew ? "Nouvelle association" : `Modifier : ${initial.nom}`}</h3>
      {err && <div className="error">{err}</div>}

      <div className="field">
        <label>Nom *</label>
        <input value={f.nom} onChange={(e) => set("nom", e.target.value)} maxLength={160} />
      </div>
      <div className="field">
        <label>Description</label>
        <textarea value={f.description} onChange={(e) => set("description", e.target.value)}
                  rows={4} maxLength={4000} />
      </div>
      <div className="field">
        <label>Contact (téléphone, email, responsable…)</label>
        <input value={f.contact} onChange={(e) => set("contact", e.target.value)} maxLength={300} />
      </div>
      <div className="field">
        <label>Lien web</label>
        <input value={f.lien_web} onChange={(e) => set("lien_web", e.target.value)}
               placeholder="https://…" maxLength={300} />
      </div>
      <div className="field" style={{ maxWidth: 160 }}>
        <label>Ordre d'affichage</label>
        <input type="number" value={f.ordre} onChange={(e) => set("ordre", parseInt(e.target.value) || 0)} />
      </div>
      <div className="field">
        <label>
          <input type="checkbox" checked={f.publie}
                 onChange={(e) => set("publie", e.target.checked)} /> Publiée (visible sur le site)
        </label>
      </div>

      <div className="field">
        <label>Logo</label>
        {isNew ? (
          <p className="muted" style={{ fontSize: "0.85em" }}>
            Enregistrez d'abord l'association, puis vous pourrez ajouter un logo.
          </p>
        ) : (
          <>
            {logoUrl && (
              <img src={`${API_BASE}${logoUrl}`} alt="logo"
                   style={{ width: 80, height: 80, objectFit: "contain", display: "block", marginBottom: 8 }} />
            )}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadLogo} />
            <span className="muted" style={{ fontSize: "0.8em", display: "block" }}>
              PNG, JPEG ou WebP, 2 Mo maximum.
            </span>
          </>
        )}
      </div>

      <button onClick={save} disabled={busy || !f.nom.trim()}>
        {busy ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}
